import { useEffect, useState } from 'react';
import { api } from '../App';
import MarketIntel from '../components/MarketIntel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, DollarSign, Truck, Package, Activity, FolderOpen, FileText, Upload, MapPin, Calculator, Building2, CreditCard, Users, BarChart3, Power, ShieldAlert, AlertTriangle, Clock, CheckCircle2, UserCircle, Mail, Eye, EyeOff, Send, ExternalLink, Plus, Trash2, Settings, Globe, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Link } from 'react-router-dom';

const DOC_TYPE_LABELS = {
  bol: 'BOL', pod: 'POD', rate_con: 'Rate Con', invoice: 'Invoice', other: 'Other'
};

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [paperwork, setPaperwork] = useState(null);
  const [loads, setLoads] = useState([]);
  const [signups, setSignups] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [brokerOverview, setBrokerOverview] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailForm, setEmailForm] = useState({ smtp_email: '', smtp_app_password: '', cc_email: '' });
  const [showEmailSetup, setShowEmailSetup] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [loadboards, setLoadboards] = useState([]);
  const [showBoardSettings, setShowBoardSettings] = useState(false);
  const [boardForm, setBoardForm] = useState({ name: '', url: '', username: '', password: '', notes: '', icon_color: '#3b82f6' });
  const [editingBoard, setEditingBoard] = useState(null);
  const [showBoardPw, setShowBoardPw] = useState({});
  const [quickEmail, setQuickEmail] = useState(null);
  const [quickEmailForm, setQuickEmailForm] = useState({ to: '', subject: '', body: '' });
  const [sendingQuickEmail, setSendingQuickEmail] = useState(false);
  const [ratePerMile, setRatePerMile] = useState('');
  const [calcMiles, setCalcMiles] = useState('');
  const [fuelVehicle, setFuelVehicle] = useState('semi');
  const [fuelZip, setFuelZip] = useState('');
  const [fuelMiles, setFuelMiles] = useState('');
  const [gasPrice, setGasPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [bankingStatus, setBankingStatus] = useState(null); // {has_banking_info}
  const [teamMissingBanking, setTeamMissingBanking] = useState(0);
  const [fleetRates, setFleetRates] = useState(null); // { "Semi Truck": {min_rpm, weight_lbs, notes}, ... }
  const [roster, setRoster] = useState([]); // Rob's assigned drivers for the leaderboard
  const [todayUsage, setTodayUsage] = useState(null); // {used_today, max_daily, pct, remaining}
  const [managedFranchises, setManagedFranchises] = useState([]); // list of orgs a broker_manager is assigned to

  const showFleetPreview = user.role === 'super_admin' || user.role === 'broker' ||
    (user.role === 'broker_manager' && user.ai_mode === 'autonomous');
  const showRosterCards = user.role === 'super_admin' || user.role === 'broker' ||
    (user.role === 'broker_manager' && user.ai_mode === 'autonomous');

  useEffect(() => {
    // Banking nudge fetch (all roles) — independent of the main promise chain
    api.get('/users/me/banking').then(r => setBankingStatus(r.data)).catch(() => {});
    if (user.role === 'super_admin' || user.role === 'broker' || user.role === 'broker_manager') {
      api.get('/admin/team-banking-status')
        .then(r => {
          const missing = (r.data.members || []).filter(m => !m.has_banking_info && m.id !== user.id).length;
          setTeamMissingBanking(missing);
        })
        .catch(() => {});
    }
    if (showFleetPreview) {
      api.get('/org/fleet-rates')
        .then(r => setFleetRates(r.data.fleet_rates || {}))
        .catch(() => setFleetRates({}));
    }
    if (showRosterCards) {
      api.get(`/users/${user.id}/assigned-drivers`)
        .then(r => setRoster(r.data.drivers || []))
        .catch(() => setRoster([]));
      api.get('/load-approvals/today-usage')
        .then(r => setTodayUsage(r.data))
        .catch(() => setTodayUsage(null));
    }
    if (user.role === 'broker_manager' && Array.isArray(user.assigned_organizations) && user.assigned_organizations.length > 0) {
      api.get('/organizations')
        .then(r => {
          const wanted = new Set(user.assigned_organizations);
          setManagedFranchises((r.data || []).filter(o => wanted.has(o.id)));
        })
        .catch(() => setManagedFranchises([]));
    }
    const promises = [
      api.get('/stats/dashboard'),
      user.role === 'broker' ? api.get('/activity-logs') : Promise.resolve({ data: [] }),
      (user.role === 'super_admin' || user.role === 'broker' || user.role === 'broker_manager') ? api.get('/stats/paperwork') : Promise.resolve({ data: null }),
      api.get('/loads'),
      user.role === 'super_admin' ? api.get('/admin/recent-signups').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      user.role === 'super_admin' ? api.get('/admin/franchise-overview').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      (user.role === 'broker' || user.role === 'super_admin') ? api.get('/stats/broker-overview').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      user.role === 'broker' ? api.get('/org/email-settings').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      api.get('/org/loadboards').catch(() => ({ data: [] })),
    ];
    Promise.all(promises)
      .then(([statsRes, logsRes, pwRes, loadsRes, signupsRes, franchisesRes, overviewRes, emailRes, boardsRes]) => {
        setStats(statsRes.data);
        setActivityLogs(logsRes.data || []);
        setPaperwork(pwRes.data);
        setLoads(loadsRes.data || []);
        setSignups(signupsRes.data || []);
        setFranchises(franchisesRes.data || []);
        setBrokerOverview(overviewRes.data);
        if (emailRes.data) {
          setEmailSettings(emailRes.data);
          setEmailForm({ smtp_email: emailRes.data.smtp_email || '', smtp_app_password: '', cc_email: emailRes.data.cc_email || '' });
        }
        setLoadboards(boardsRes.data || []);
      })
      .catch(err => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [user.role]);

  if (loading) {
    return <div className="p-8">Loading dashboard...</div>;
  }

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return 'text-green-600 bg-green-50';
      case 'updated': return 'text-blue-600 bg-blue-50';
      case 'deleted': return 'text-red-600 bg-red-50';
      case 'promoted': return 'text-purple-600 bg-purple-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  const saveEmailSettings = () => {
    const payload = {};
    if (emailForm.smtp_email) payload.smtp_email = emailForm.smtp_email;
    if (emailForm.smtp_app_password) payload.smtp_app_password = emailForm.smtp_app_password;
    if (emailForm.cc_email) payload.cc_email = emailForm.cc_email;
    api.put('/org/email-settings', payload)
      .then(() => {
        toast.success('Email settings saved!');
        setShowEmailSetup(false);
        setEmailSettings({ smtp_email: emailForm.smtp_email, has_password: true, cc_email: emailForm.cc_email });
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to save'));
  };

  const testEmail = () => {
    setEmailTesting(true);
    api.post('/org/email-settings/test')
      .then(res => toast.success(res.data.message))
      .catch(err => toast.error(err.response?.data?.detail || 'Test failed'))
      .finally(() => setEmailTesting(false));
  };

  const addLoadboard = () => {
    if (!boardForm.name || !boardForm.url) { toast.error('Name and URL are required'); return; }
    const url = boardForm.url.startsWith('http') ? boardForm.url : `https://${boardForm.url}`;
    const payload = { ...boardForm, url };
    const promise = editingBoard
      ? api.put(`/org/loadboards/${editingBoard.id}`, payload)
      : api.post('/org/loadboards', payload);
    promise
      .then(res => {
        toast.success(res.data.message);
        setBoardForm({ name: '', url: '', username: '', password: '', notes: '', icon_color: '#3b82f6' });
        setEditingBoard(null);
        api.get('/org/loadboards').then(r => setLoadboards(r.data || []));
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const deleteLoadboard = (id) => {
    api.delete(`/org/loadboards/${id}`)
      .then(() => { toast.success('Load board removed'); setLoadboards(loadboards.filter(b => b.id !== id)); })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const sendQuickEmail = () => {
    if (!quickEmailForm.to || !quickEmailForm.subject || !quickEmailForm.body) {
      toast.error('Fill in all fields');
      return;
    }
    setSendingQuickEmail(true);
    api.post('/org/send-email', {
      to: quickEmailForm.to,
      subject: quickEmailForm.subject,
      body: quickEmailForm.body
    })
      .then(res => {
        toast.success('Email sent!');
        setQuickEmail(null);
        setQuickEmailForm({ to: '', subject: '', body: '' });
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to send'))
      .finally(() => setSendingQuickEmail(false));
  };

  const statCards = [
    { title: 'Total Carriers', value: stats?.total_carriers || 0, icon: Truck, color: 'blue' },
    { title: 'Total Brokers', value: stats?.total_shippers || 0, icon: Package, color: 'green' },
    { title: 'Total Loads', value: stats?.total_loads || 0, icon: TrendingUp, color: 'purple' },
    { title: 'Total Revenue', value: `$${stats?.total_revenue?.toLocaleString() || 0}`, icon: DollarSign, color: 'emerald' },
    { title: 'Total Profit', value: `$${stats?.total_profit?.toLocaleString() || 0}`, icon: DollarSign, color: 'yellow' },
    { title: 'Avg Margin', value: `${stats?.avg_margin || 0}%`, icon: TrendingUp, color: 'indigo' }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Welcome back, {user.name}!</h1>
        <p className="text-slate-600 mt-1">Here's your freight broker dashboard overview</p>
      </div>

      {/* Daily Booking Circuit Breaker — warns Rob when today's autos hit 80% of max */}
      {todayUsage && todayUsage.pct >= 80 && (
        <div
          className={`rounded-lg p-4 border-l-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
            todayUsage.pct >= 100
              ? 'bg-red-50 border-red-500'
              : 'bg-amber-50 border-amber-500'
          }`}
          data-testid="circuit-breaker-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${todayUsage.pct >= 100 ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className={`text-sm font-semibold ${todayUsage.pct >= 100 ? 'text-red-900' : 'text-amber-900'}`}>
                {todayUsage.pct >= 100
                  ? `AI locked — you hit today's ${todayUsage.max_daily}-book cap`
                  : `Approaching daily cap — ${todayUsage.used_today} of ${todayUsage.max_daily} auto-books today (${todayUsage.pct}%)`}
              </p>
              <p className={`text-xs mt-0.5 ${todayUsage.pct >= 100 ? 'text-red-800' : 'text-amber-800'}`}>
                {todayUsage.pct >= 100
                  ? 'Raise your Max Bookings/Day in Fleet Rates or wait until tomorrow.'
                  : `${todayUsage.remaining} slot${todayUsage.remaining === 1 ? '' : 's'} left before the AI stops booking.`}
              </p>
            </div>
          </div>
          <Link to="/fleet-rates">
            <Button size="sm" variant="outline" data-testid="circuit-breaker-cta">
              Adjust Cap →
            </Button>
          </Link>
        </div>
      )}

      {/* Banking Nudge — MY missing banking */}
      {bankingStatus && !bankingStatus.has_banking_info && (
        <div
          className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="banking-nudge-self"
        >
          <div className="flex items-start gap-3">
            <Landmark className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Add your direct-deposit banking</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Payouts on your booked loads can't hit your account until you add routing + account numbers. Takes 60 seconds.
              </p>
            </div>
          </div>
          <Link to="/profile">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="banking-nudge-self-cta">
              Set up banking →
            </Button>
          </Link>
        </div>
      )}

      {/* Banking Nudge — TEAM has missing (broker/manager/super_admin) */}
      {teamMissingBanking > 0 && (
        <div
          className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="banking-nudge-team"
        >
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">
                {teamMissingBanking} team member{teamMissingBanking > 1 ? 's are' : ' is'} missing banking info
              </p>
              <p className="text-xs text-blue-800 mt-0.5">
                They won't receive their payouts until they add it. Send a friendly nudge or view roster.
              </p>
            </div>
          </div>
          <Link to="/profile">
            <Button size="sm" variant="outline" className="border-blue-500 text-blue-700 hover:bg-blue-100" data-testid="banking-nudge-team-cta">
              View team roster →
            </Button>
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, idx) => (
          <Card key={idx} className="card-hover" data-testid={`stat-card-${idx}`}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                {stat.title}
              </CardTitle>
              <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Market Intelligence */}
      <MarketIntel user={user} />

      {/* Managed Franchises Panel — shown only when Rob (broker_manager) has franchises assigned to oversee */}
      {user.role === 'broker_manager' && managedFranchises.length > 0 && (
        <Card data-testid="managed-franchises-panel" className="border-indigo-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Managed Franchises
              <span className="text-[10px] uppercase tracking-widest text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded ml-1">
                {managedFranchises.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {managedFranchises.map(o => (
                <Link
                  key={o.id}
                  to="/agents"
                  className="p-3 rounded-lg bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors block"
                  data-testid={`franchise-jump-${o.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{o.company_name}</p>
                      <p className="text-[11px] text-slate-500">Code: {o.master_code} · {(o.plan || o.subscription_tier || 'basic').toString().toUpperCase()}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-500">Active</p>
                      <p className="text-sm font-bold text-blue-700">{o.stat_active_loads ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-500">Agents</p>
                      <p className="text-sm font-bold text-purple-700">{o.stat_agents ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-500">30-day Rev</p>
                      <p className="text-sm font-bold text-emerald-700 font-mono">${(o.stat_revenue_30d || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fleet Rates Live Preview — visible to Phase 3 dispatchers, brokers, super admin */}
      {showFleetPreview && fleetRates && (
        <Card data-testid="fleet-rates-preview" className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-5 h-5 text-amber-600" />
                Auto-Book Rules · Fleet Rates
                <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">Live</span>
              </CardTitle>
              <Link
                to="/fleet-rates"
                className="text-xs text-amber-700 hover:text-amber-900 hover:underline font-medium"
                data-testid="edit-fleet-rates-link"
              >
                Edit →
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {Object.keys(fleetRates).length === 0 ? (
              <p className="text-sm text-slate-500">
                No fleet rates set yet — <Link to="/fleet-rates" className="text-amber-700 underline">configure minimums</Link> so autonomous AI knows your floor.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(fleetRates).map(([truckType, cfg]) => (
                  <div
                    key={truckType}
                    className="bg-white/70 border border-amber-200 rounded-lg p-3"
                    data-testid={`fleet-rate-${truckType.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{truckType}</p>
                    <p className="text-2xl font-bold text-amber-800 mt-0.5">
                      ${Number(cfg.min_rpm || 0).toFixed(2)}
                      <span className="text-xs font-normal text-slate-500 ml-1">/mi min</span>
                    </p>
                    {cfg.weight_lbs ? (
                      <p className="text-[11px] text-slate-500 mt-0.5">≤ {Number(cfg.weight_lbs).toLocaleString()} lbs</p>
                    ) : null}
                    {cfg.notes ? (
                      <p className="text-[10px] text-slate-500 mt-1 line-clamp-2 italic">{cfg.notes}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver Efficiency Leaderboard — Rob's 5 drivers ranked by $/mile */}
      {showRosterCards && roster && roster.length > 0 && (
        <Card data-testid="driver-leaderboard" className="border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Driver Efficiency Leaderboard
              <span className="text-[10px] uppercase tracking-widest text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded ml-1">YTD $/Mile</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[...roster]
                .sort((a, b) => (b.avg_rpm || 0) - (a.avg_rpm || 0))
                .map((d, i) => {
                  const rank = i + 1;
                  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;
                  const rpm = Number(d.avg_rpm || 0);
                  const maxRpm = Math.max(...roster.map(x => x.avg_rpm || 0), 1);
                  const barPct = maxRpm > 0 ? Math.round((rpm / maxRpm) * 100) : 0;
                  return (
                    <div
                      key={d.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 border border-slate-200"
                      data-testid={`leaderboard-row-${d.id}`}
                    >
                      <span className="text-lg w-10 text-center flex-shrink-0">{medal}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate">{d.name}</p>
                          <p className="text-sm font-mono font-bold text-purple-700">${rpm.toFixed(2)}/mi</p>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-purple-400 to-purple-600" style={{ width: `${barPct}%` }} />
                          </div>
                          <p className="text-[10px] text-slate-500 flex-shrink-0">
                            {d.ytd_loads || 0} loads · ${(d.ytd_gross || 0).toLocaleString()} gross
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            {roster.every(d => (d.avg_rpm || 0) === 0) && (
              <p className="text-xs text-slate-500 mt-2 italic text-center">
                Ranking activates once your drivers complete their first load this year.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Load Board Quick Access */}
      <Card data-testid="loadboard-quick-access">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Load Boards
            </CardTitle>
            {(user.role === 'super_admin' || user.role === 'broker') && (
              <Button size="sm" variant="outline" onClick={() => setShowBoardSettings(true)} data-testid="manage-loadboards-btn">
                <Settings className="w-3.5 h-3.5 mr-1" /> Manage
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadboards.length === 0 ? (
            <div className="text-center py-4">
              <Globe className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No load boards configured yet.</p>
              {(user.role === 'super_admin' || user.role === 'broker') && (
                <Button size="sm" variant="outline" className="mt-2" onClick={() => setShowBoardSettings(true)} data-testid="add-first-board-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Load Board
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {loadboards.map(board => (
                  <div key={board.id} className="rounded-xl border-2 overflow-hidden hover:shadow-md transition-all" style={{ borderColor: board.icon_color + '40' }} data-testid={`loadboard-btn-${board.id}`}>
                    <a href={board.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-3 group cursor-pointer"
                      style={{ backgroundColor: board.icon_color + '08' }}
                      onClick={() => {
                        if (board.username) {
                          navigator.clipboard.writeText(board.username).catch(() => {});
                          toast.success(`Username "${board.username}" copied! Paste it on the login page.`, { duration: 5000 });
                        }
                      }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: board.icon_color }}>
                        {board.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-slate-900 group-hover:text-blue-700">{board.name}</p>
                        {board.notes && <p className="text-xs text-slate-500">{board.notes}</p>}
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400 ml-1 group-hover:text-blue-600 shrink-0" />
                    </a>
                    {(board.username || board.has_password) && (
                      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-xs flex items-center justify-between">
                        <div className="space-y-0.5">
                          {board.username && <p className="text-slate-600"><span className="text-slate-400">Login:</span> <strong>{board.username}</strong></p>}
                          {board.password && board.password !== '***' && (
                            <p className="text-slate-600"><span className="text-slate-400">Pass:</span> <strong className="font-mono">{board.password}</strong></p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          {board.username && (
                            <a
                              href={board.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(board.username).catch(() => {});
                                toast.success(`Opening ${board.name} — username "${board.username}" copied! Just paste it.`, { duration: 5000 });
                              }}
                              className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-medium no-underline inline-block"
                              data-testid={`copy-user-${board.id}`}
                            >
                              Copy User & Open
                            </a>
                          )}
                          {board.password && board.password !== '***' && (
                            <a
                              href={board.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(board.password).catch(() => {});
                                toast.success(`Opening ${board.name} — password copied! Just paste it.`, { duration: 5000 });
                              }}
                              className="px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 text-xs font-medium no-underline inline-block"
                              data-testid={`copy-pass-${board.id}`}
                            >
                              Copy Pass & Open
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { setQuickEmail(true); setQuickEmailForm({ to: '', subject: 'Load Inquiry', body: 'Hi,\n\nI saw your load posted on the board and I\'m interested. We have trucks available in the area.\n\nPlease let me know the details.\n\nThank you' }); }} data-testid="quick-email-broker-btn">
                <Mail className="w-4 h-4 mr-1" /> Email Load Broker
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Load Board Management Dialog */}
      <Dialog open={showBoardSettings} onOpenChange={setShowBoardSettings}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Manage Load Boards
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Add the load boards your team uses. Agents see quick-access buttons on their dashboard.</p>
            <div className="border rounded-lg p-3 space-y-3 bg-slate-50">
              <p className="text-xs font-semibold text-slate-500 uppercase">{editingBoard ? 'Edit Board' : 'Add New Board'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Board Name *</Label><Input placeholder="DAT Load Board" value={boardForm.name} onChange={e => setBoardForm({...boardForm, name: e.target.value})} data-testid="board-name-input" /></div>
                <div><Label className="text-xs">Login URL *</Label><Input placeholder="https://power.dat.com/login" value={boardForm.url} onChange={e => setBoardForm({...boardForm, url: e.target.value})} data-testid="board-url-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Username / Email</Label><Input placeholder="your-login" value={boardForm.username} onChange={e => setBoardForm({...boardForm, username: e.target.value})} data-testid="board-username-input" /></div>
                <div><Label className="text-xs">Password</Label><Input type="password" placeholder="saved securely" value={boardForm.password} onChange={e => setBoardForm({...boardForm, password: e.target.value})} data-testid="board-password-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Notes</Label><Input placeholder="e.g., Flat bed loads" value={boardForm.notes} onChange={e => setBoardForm({...boardForm, notes: e.target.value})} data-testid="board-notes-input" /></div>
                <div>
                  <Label className="text-xs">Color</Label>
                  <div className="flex gap-2 mt-1">
                    {['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#f97316'].map(c => (
                      <button key={c} type="button" onClick={() => setBoardForm({...boardForm, icon_color: c})}
                        className={`w-6 h-6 rounded-full border-2 ${boardForm.icon_color === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addLoadboard} data-testid="save-board-btn">{editingBoard ? 'Update' : 'Add Board'}</Button>
                {editingBoard && <Button size="sm" variant="outline" onClick={() => { setEditingBoard(null); setBoardForm({ name: '', url: '', username: '', password: '', notes: '', icon_color: '#3b82f6' }); }}>Cancel</Button>}
              </div>
            </div>
            {loadboards.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Configured Boards ({loadboards.length})</p>
                {loadboards.map(board => (
                  <div key={board.id} className="flex items-center gap-3 p-3 border rounded-lg bg-white" data-testid={`board-item-${board.id}`}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: board.icon_color }}>{board.name.charAt(0).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{board.name}</p>
                      <p className="text-xs text-slate-500 truncate">{board.url}</p>
                      {board.username && <p className="text-xs text-blue-600">Login: {board.username}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => { setEditingBoard(board); setBoardForm({ name: board.name, url: board.url, username: board.username || '', password: '', notes: board.notes || '', icon_color: board.icon_color || '#3b82f6' }); }}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteLoadboard(board.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Quick Add Popular Boards</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'DAT Load Board', url: 'https://power.dat.com', color: '#3b82f6' },
                  { name: 'Truckstop', url: 'https://truckstop.com/login', color: '#10b981' },
                  { name: '123Loadboard', url: 'https://www.123loadboard.com/signin/', color: '#f59e0b' },
                  { name: 'Direct Freight', url: 'https://app.directfreight.com/login', color: '#ef4444' },
                  { name: 'Amazon Relay', url: 'https://relay.amazon.com', color: '#f97316' },
                  { name: 'Sylectus', url: 'https://www.sylectus.com', color: '#8b5cf6' },
                ].filter(qb => !loadboards.some(b => b.name === qb.name)).map(qb => (
                  <button key={qb.name} type="button"
                    onClick={() => setBoardForm({ name: qb.name, url: qb.url, username: '', password: '', notes: '', icon_color: qb.color })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    data-testid={`quick-add-${qb.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <Plus className="w-3 h-3 inline mr-1" />{qb.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Email Broker Dialog */}
      <Dialog open={!!quickEmail} onOpenChange={() => setQuickEmail(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              Email Load Broker
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Found a load on the board? Email the broker directly to secure it fast.</p>
            <div>
              <Label className="text-xs">Broker's Email *</Label>
              <Input placeholder="broker@company.com" value={quickEmailForm.to} onChange={e => setQuickEmailForm({...quickEmailForm, to: e.target.value})} data-testid="quick-email-to" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={quickEmailForm.subject} onChange={e => setQuickEmailForm({...quickEmailForm, subject: e.target.value})} data-testid="quick-email-subject" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea
                rows={5}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={quickEmailForm.body}
                onChange={e => setQuickEmailForm({...quickEmailForm, body: e.target.value})}
                data-testid="quick-email-body"
              />
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={sendQuickEmail} disabled={sendingQuickEmail} data-testid="send-quick-email-btn">
              <Send className="w-4 h-4 mr-2" />
              {sendingQuickEmail ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-slate-600">Navigate to:</p>
            <div className="flex flex-wrap gap-2">
              <a href="/carriers" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Manage Carriers
              </a>
              <a href="/shippers" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Manage Brokers
              </a>
              <a href="/loads" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Book New Load
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Franchise Email Setup - Broker Only */}
        {user.role === 'broker' && (
          <Card data-testid="email-settings-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="w-5 h-5 text-blue-600" />
                Email Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showEmailSetup ? (
                <div>
                  {emailSettings?.smtp_email ? (
                    <div className="space-y-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-800">Email configured</p>
                        <p className="text-xs text-green-600 mt-0.5">{emailSettings.smtp_email}</p>
                      </div>
                      {emailSettings?.cc_email && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs font-medium text-blue-700">CC on all emails:</p>
                          <p className="text-xs text-blue-600">{emailSettings.cc_email}</p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setShowEmailSetup(true)} data-testid="edit-email-btn">
                          Change
                        </Button>
                        <Button size="sm" variant="outline" onClick={testEmail} disabled={emailTesting} data-testid="test-email-btn">
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {emailTesting ? 'Sending...' : 'Test Email'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-500">Set up your Gmail to email agents directly from the platform.</p>
                      <Button size="sm" onClick={() => setShowEmailSetup(true)} data-testid="setup-email-btn">
                        <Mail className="w-3.5 h-3.5 mr-1" />
                        Configure Email
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Gmail Address</Label>
                    <Input
                      type="email"
                      placeholder="your-email@gmail.com"
                      value={emailForm.smtp_email}
                      onChange={e => setEmailForm({ ...emailForm, smtp_email: e.target.value })}
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">App Password</Label>
                    <div className="relative">
                      <Input
                        type={showPw ? 'text' : 'password'}
                        placeholder="xxxx xxxx xxxx xxxx"
                        value={emailForm.smtp_app_password}
                        onChange={e => setEmailForm({ ...emailForm, smtp_app_password: e.target.value })}
                        data-testid="email-password-input"
                      />
                      <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPw(!showPw)}>
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Generate at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 underline">Google App Passwords</a>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">CC Email (copied on all outgoing emails)</Label>
                    <Input
                      type="email"
                      placeholder="admin@yourcompany.com"
                      value={emailForm.cc_email}
                      onChange={e => setEmailForm({ ...emailForm, cc_email: e.target.value })}
                      data-testid="cc-email-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Admin/broker gets CC'd on every email sent from the platform</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEmailSettings} data-testid="save-email-btn">Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setShowEmailSetup(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {user.role === 'broker' && activityLogs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {activityLogs.slice(0, 10).map((log) => (
                  <div key={log.id} className="flex items-start space-x-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-900 font-medium truncate">
                        {log.user_name}
                        {log.agent_pin && (
                          <span className="ml-2 text-blue-600 font-bold text-xs">
                            (Agent ID: {log.agent_pin})
                          </span>
                        )}
                      </p>
                      <p className="text-slate-600 text-xs truncate">
                        {log.action} {log.resource_type}: {log.resource_name}
                      </p>
                      <p className="text-slate-400 text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Broker Operations Overview */}
      {(user.role === 'broker' || user.role === 'super_admin') && brokerOverview && (
        <div className="space-y-6" data-testid="broker-overview">
          {/* Operations Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-blue-500" data-testid="overview-loads-today">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Loads Today</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{brokerOverview.loads?.today || 0}</p>
                <p className="text-xs text-blue-600 mt-1">${(brokerOverview.loads?.revenue_today || 0).toLocaleString()} revenue</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500" data-testid="overview-active-loads">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Active Loads</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{brokerOverview.loads?.active || 0}</p>
                <p className="text-xs text-green-600 mt-1">In transit / dispatched</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500" data-testid="overview-team-size">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Team Size</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{brokerOverview.team?.total_agents || 0}</p>
                <p className="text-xs text-purple-600 mt-1">{brokerOverview.team?.total_managers || 0} manager{brokerOverview.team?.total_managers !== 1 ? 's' : ''}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${brokerOverview.compliance?.critical > 0 ? 'border-l-red-500' : brokerOverview.compliance?.warnings > 0 ? 'border-l-amber-500' : 'border-l-green-500'}`} data-testid="overview-compliance">
              <CardContent className="pt-5 pb-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Compliance</p>
                {brokerOverview.compliance?.critical > 0 ? (
                  <>
                    <p className="text-3xl font-bold text-red-600 mt-1">{brokerOverview.compliance.critical}</p>
                    <p className="text-xs text-red-600 mt-1">Critical alerts</p>
                  </>
                ) : brokerOverview.compliance?.warnings > 0 ? (
                  <>
                    <p className="text-3xl font-bold text-amber-600 mt-1">{brokerOverview.compliance.warnings}</p>
                    <p className="text-xs text-amber-600 mt-1">Expiring soon</p>
                  </>
                ) : (
                  <>
                    <p className="text-3xl font-bold text-green-600 mt-1">0</p>
                    <p className="text-xs text-green-600 mt-1">All clear</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Agent Performance & Compliance Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Agent Performance */}
            <Card data-testid="agent-performance-widget">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserCircle className="w-5 h-5 text-purple-600" />
                  Agent Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {brokerOverview.agent_performance?.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No agents yet. Add agents from User Management.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-slate-500 pb-1 border-b">
                      <span>Agent</span>
                      <span className="text-center">PIN</span>
                      <span className="text-right">Loads</span>
                    </div>
                    {brokerOverview.agent_performance?.map((agent, i) => (
                      <div key={i} className="grid grid-cols-3 gap-2 items-center text-sm py-1.5 border-b border-slate-50" data-testid={`agent-perf-${i}`}>
                        <span className="font-medium truncate">{agent.name}</span>
                        <span className="text-center text-blue-600 font-mono text-xs">{agent.pin}</span>
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-slate-100 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ width: `${Math.min(100, (agent.loads / Math.max(...brokerOverview.agent_performance.map(a => a.loads), 1)) * 100)}%` }}
                            />
                          </div>
                          <span className="font-bold text-slate-900 w-8 text-right">{agent.loads}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Alerts Preview */}
            <Card data-testid="compliance-alerts-widget">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                    Compliance Alerts
                  </CardTitle>
                  <a href="/compliance" className="text-xs text-blue-600 hover:underline" data-testid="view-all-compliance-link">View All</a>
                </div>
              </CardHeader>
              <CardContent>
                {(!brokerOverview.compliance?.expiring_soon || brokerOverview.compliance.expiring_soon.length === 0) ? (
                  <div className="text-center py-4">
                    <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-700">All Clear</p>
                    <p className="text-xs text-slate-500">No upcoming expirations</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {brokerOverview.compliance.expiring_soon.map((item, i) => (
                      <div key={i} className={`flex items-center gap-3 p-2 rounded-lg ${item.severity === 'critical' ? 'bg-red-50' : 'bg-amber-50'}`} data-testid={`compliance-item-${i}`}>
                        {item.severity === 'critical' ? (
                          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                          <p className={`text-xs ${item.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                            {item.field} {item.days < 0 ? `expired ${Math.abs(item.days)}d ago` : `expires in ${item.days}d`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Load Status Breakdown */}
          {brokerOverview.loads?.status_breakdown && Object.keys(brokerOverview.loads.status_breakdown).length > 0 && (
            <Card data-testid="load-status-breakdown">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Load Status Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(brokerOverview.loads.status_breakdown).map(([status, count]) => {
                    const colors = {
                      pending: 'bg-slate-100 text-slate-700',
                      dispatched: 'bg-blue-100 text-blue-700',
                      picked_up: 'bg-indigo-100 text-indigo-700',
                      in_transit: 'bg-cyan-100 text-cyan-700',
                      delivered: 'bg-emerald-100 text-emerald-700',
                      dropped_off: 'bg-green-100 text-green-700',
                      completed: 'bg-green-100 text-green-700',
                      cancelled: 'bg-red-100 text-red-700',
                    };
                    return (
                      <div key={status} className={`px-4 py-2 rounded-lg ${colors[status] || 'bg-slate-100 text-slate-700'}`} data-testid={`status-${status}`}>
                        <p className="text-lg font-bold">{count}</p>
                        <p className="text-xs capitalize">{status.replace('_', ' ')}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Team Activity */}
          {brokerOverview.recent_activity?.length > 0 && (
            <Card data-testid="recent-team-activity">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-5 h-5 text-green-600" />
                  Team Activity (Last 24h)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {brokerOverview.recent_activity.map((log, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm py-1.5 border-b border-slate-50" data-testid={`team-activity-${i}`}>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                        log.action === 'created' ? 'bg-green-50 text-green-700' :
                        log.action === 'updated' ? 'bg-blue-50 text-blue-700' :
                        log.action === 'deleted' ? 'bg-red-50 text-red-700' :
                        'bg-slate-50 text-slate-700'
                      }`}>{log.action}</span>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{log.user_name}</span>
                        {log.agent_pin && <span className="text-blue-600 text-xs ml-1">(PIN: {log.agent_pin})</span>}
                        <span className="text-slate-500"> {log.action} {log.resource_type}</span>
                        {log.resource_name && <span className="text-slate-700">: {log.resource_name}</span>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{new Date(log.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Per Mile Analysis & Rate Calculator */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="mile-widgets">
        {/* Driver Pay Per Mile */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="w-5 h-5 text-green-600" />
              Driver Pay Per Mile
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loads.filter(l => l.estimated_miles > 0).length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No loads with mileage yet. Go to Loads and click "Calculate Mileage".</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-slate-500 pb-1 border-b">
                  <span className="col-span-2">Load</span>
                  <span>Miles</span>
                  <span>Carrier Rate</span>
                  <span>$/Mile</span>
                </div>
                {loads.filter(l => l.estimated_miles > 0).map(load => {
                  const perMile = (load.rate_to_carrier / load.estimated_miles).toFixed(2);
                  const color = perMile >= 3 ? 'text-green-600' : perMile >= 2 ? 'text-yellow-600' : 'text-red-600';
                  return (
                    <div key={load.id} className="grid grid-cols-5 gap-2 items-center text-sm py-1.5 border-b border-slate-50" data-testid={`ppm-${load.id}`}>
                      <span className="col-span-2 font-medium truncate">{load.load_number}</span>
                      <span>{load.estimated_miles}</span>
                      <span>${load.rate_to_carrier}</span>
                      <span className={`font-bold ${color}`}>${perMile}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rate Calculator */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calculator className="w-5 h-5 text-blue-600" />
              Rate Calculator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">Enter your desired rate per mile and the miles to see what you should charge.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Your Rate ($/mile)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 2.50"
                  value={ratePerMile}
                  onChange={e => setRatePerMile(e.target.value)}
                  data-testid="rate-per-mile-input"
                />
              </div>
              <div>
                <Label className="text-xs">Miles</Label>
                <Input
                  type="number"
                  placeholder="e.g. 500"
                  value={calcMiles}
                  onChange={e => setCalcMiles(e.target.value)}
                  data-testid="calc-miles-input"
                />
              </div>
            </div>
            {ratePerMile && calcMiles && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2" data-testid="rate-calc-result">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">You should ask for:</span>
                  <span className="text-2xl font-bold text-green-600">
                    ${(parseFloat(ratePerMile) * parseFloat(calcMiles)).toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-slate-400 space-y-0.5">
                  <p>{calcMiles} miles x ${ratePerMile}/mi = ${(parseFloat(ratePerMile) * parseFloat(calcMiles)).toFixed(2)}</p>
                </div>
              </div>
            )}
            {loads.filter(l => l.estimated_miles > 0).length > 0 && (
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Quick fill from existing loads:</Label>
                <div className="flex flex-wrap gap-2">
                  {loads.filter(l => l.estimated_miles > 0).slice(0, 5).map(l => (
                    <button
                      key={l.id}
                      onClick={() => setCalcMiles(String(l.estimated_miles))}
                      className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      {l.load_number.slice(-6)} ({l.estimated_miles} mi)
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fuel Cost Calculator */}
      <Card data-testid="fuel-calculator">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Truck className="w-5 h-5 text-orange-600" />
            Fuel Cost Calculator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Estimate fuel costs for negotiating rates. Enter miles and vehicle type.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Vehicle Type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={fuelVehicle}
                onChange={e => setFuelVehicle(e.target.value)}
                data-testid="fuel-vehicle-select"
              >
                <option value="semi">Semi Truck (5.5 MPG)</option>
                <option value="box_truck">Box Truck (10 MPG)</option>
                <option value="cargo_van">Cargo Van (18 MPG)</option>
                <option value="sprinter">Sprinter Van (15 MPG)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Miles</Label>
              <Input type="number" placeholder="e.g. 800" value={fuelMiles} onChange={e => setFuelMiles(e.target.value)} data-testid="fuel-miles-input" />
            </div>
            <div>
              <Label className="text-xs">Gas Price ($/gal)</Label>
              <div className="flex gap-1">
                <Input type="number" step="0.01" placeholder="3.50" value={gasPrice} onChange={e => setGasPrice(e.target.value)} data-testid="fuel-price-input" />
                <Button size="sm" variant="outline" className="shrink-0 px-2" onClick={() => {
                  if (!fuelZip || fuelZip.length < 5) { toast.error('Enter a 5-digit zip code'); return; }
                  api.get(`/fuel-price?zip=${fuelZip}`).then(res => { setGasPrice(String(res.data.price)); toast.success(`Gas: $${res.data.price}/gal (${res.data.source})`); }).catch(() => toast.error('Could not get gas price, enter manually'));
                }} data-testid="lookup-gas-btn">
                  <MapPin className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Zip Code (for price lookup)</Label>
              <Input placeholder="75201" maxLength={5} value={fuelZip} onChange={e => setFuelZip(e.target.value)} data-testid="fuel-zip-input" />
            </div>
          </div>

          {fuelMiles && gasPrice && (
            <div className="bg-orange-50 rounded-lg p-4 space-y-3" data-testid="fuel-calc-result">
              {(() => {
                const mpg = { semi: 5.5, box_truck: 10, cargo_van: 18, sprinter: 15 }[fuelVehicle];
                const vehicleLabel = { semi: 'Semi Truck', box_truck: 'Box Truck', cargo_van: 'Cargo Van', sprinter: 'Sprinter Van' }[fuelVehicle];
                const miles = parseFloat(fuelMiles);
                const price = parseFloat(gasPrice);
                const gallons = miles / mpg;
                const fuelCost = gallons * price;
                const costPerMile = fuelCost / miles;
                return (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Estimated Fuel Cost:</span>
                      <span className="text-2xl font-bold text-orange-600">${fuelCost.toFixed(2)}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-slate-500">Vehicle</p>
                        <p className="font-bold">{vehicleLabel}</p>
                        <p className="text-slate-400">{mpg} MPG</p>
                      </div>
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-slate-500">Gallons Needed</p>
                        <p className="font-bold text-blue-600">{gallons.toFixed(1)} gal</p>
                      </div>
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-slate-500">Fuel Cost/Mile</p>
                        <p className="font-bold text-red-600">${costPerMile.toFixed(3)}/mi</p>
                      </div>
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-slate-500">At ${price}/gal</p>
                        <p className="font-bold">{miles} miles</p>
                      </div>
                    </div>
                    <div className="border-t pt-2 text-xs text-slate-600 space-y-1">
                      <p className="font-medium">Negotiation tip:</p>
                      <p>Minimum rate to cover fuel: <strong className="text-red-600">${fuelCost.toFixed(2)}</strong></p>
                      <p>Rate at $2.00/mi: <strong className="text-green-600">${(miles * 2).toFixed(2)}</strong> (profit after fuel: ${((miles * 2) - fuelCost).toFixed(2)})</p>
                      <p>Rate at $2.50/mi: <strong className="text-green-600">${(miles * 2.5).toFixed(2)}</strong> (profit after fuel: ${((miles * 2.5) - fuelCost).toFixed(2)})</p>
                      <p>Rate at $3.00/mi: <strong className="text-green-600">${(miles * 3).toFixed(2)}</strong> (profit after fuel: ${((miles * 3) - fuelCost).toFixed(2)})</p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {loads.filter(l => l.estimated_miles > 0).length > 0 && (
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Quick fill miles from loads:</Label>
              <div className="flex flex-wrap gap-2">
                {loads.filter(l => l.estimated_miles > 0).slice(0, 5).map(l => (
                  <button key={l.id} onClick={() => setFuelMiles(String(l.estimated_miles))}
                    className="text-xs px-2 py-1 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">
                    {l.load_number.slice(-6)} ({l.estimated_miles} mi)
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Franchise Signup Feed - Super Admin Only */}
      {user.role === 'super_admin' && (
        <Card data-testid="franchise-signups-widget">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-5 h-5 text-blue-600" />
              Recent Franchise Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {signups.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No franchise signups yet.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {signups.map((s, i) => {
                  const isPaid = s.payment_status === 'paid';
                  return (
                    <div key={s.session_id || i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`signup-${i}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isPaid ? 'bg-green-100' : 'bg-yellow-100'}`}>
                          <CreditCard className={`w-4 h-4 ${isPaid ? 'text-green-600' : 'text-yellow-600'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{s.company_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className={`px-1.5 py-0.5 rounded font-medium ${
                              s.plan === 'extreme' ? 'bg-purple-100 text-purple-700' :
                              s.plan === 'pro' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-200 text-slate-700'
                            }`}>{(s.plan || '').toUpperCase()}</span>
                            <span>${s.setup_fee || s.amount || 0} setup</span>
                            {s.master_code && <span className="font-mono font-bold text-blue-600">Code: {s.master_code}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {isPaid ? 'PAID' : (s.payment_status || 'pending').toUpperCase()}
                        </span>
                        <p className="text-xs text-slate-400 mt-1">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Franchise Overview Grid - Super Admin Only */}
      {user.role === 'super_admin' && franchises.length > 0 && (() => {
        const toggleStatus = async (orgId, currentStatus) => {
          const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
          try {
            await api.put(`/admin/organizations/${orgId}/status`, { status: newStatus });
            setFranchises(prev => prev.map(f => f.id === orgId ? { ...f, status: newStatus } : f));
            toast.success(`Franchise ${newStatus === 'active' ? 'reactivated' : 'suspended'}`);
          } catch { toast.error('Failed to update status'); }
        };
        return (
        <Card data-testid="franchise-overview-widget">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-base">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Franchise Overview
              </span>
              <span className="text-xs font-normal text-slate-400">{franchises.length} franchise{franchises.length !== 1 ? 's' : ''}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="franchise-overview-table">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wider">
                    <th className="pb-2 pr-3 font-medium">Franchise</th>
                    <th className="pb-2 px-2 font-medium text-center">Plan</th>
                    <th className="pb-2 px-2 font-medium text-center">Status</th>
                    <th className="pb-2 px-2 font-medium text-right">Carriers</th>
                    <th className="pb-2 px-2 font-medium text-right">Loads</th>
                    <th className="pb-2 px-2 font-medium text-right">Active</th>
                    <th className="pb-2 px-2 font-medium text-right">Drivers</th>
                    <th className="pb-2 px-2 font-medium text-right">Agents</th>
                    <th className="pb-2 px-2 font-medium text-right">Revenue</th>
                    <th className="pb-2 pl-2 font-medium text-right">Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {franchises.map((f, i) => {
                    const isActive = (f.status || 'active') === 'active';
                    return (
                      <tr key={f.id} className="hover:bg-slate-50 transition-colors" data-testid={`franchise-row-${i}`}>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold ${isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-red-100 text-red-600'}`}>
                              {(f.company_name || '?')[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 truncate max-w-[180px]">{f.company_name}</p>
                              <p className="text-[10px] text-slate-400 font-mono">Code: {f.master_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            f.plan === 'extreme' ? 'bg-purple-100 text-purple-700' :
                            f.plan === 'pro' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{(f.plan || 'basic').toUpperCase()}</span>
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`toggle-status-${i}`}
                            className={`h-7 px-2 text-[10px] font-bold gap-1 ${isActive ? 'text-green-700 hover:text-red-600 hover:bg-red-50' : 'text-red-600 hover:text-green-700 hover:bg-green-50'}`}
                            onClick={() => toggleStatus(f.id, f.status || 'active')}
                          >
                            <Power className="w-3 h-3" />
                            {isActive ? 'Active' : 'Suspended'}
                          </Button>
                        </td>
                        <td className="py-2.5 px-2 text-right font-medium text-slate-700">{f.carriers}</td>
                        <td className="py-2.5 px-2 text-right font-medium text-slate-700">{f.loads}</td>
                        <td className="py-2.5 px-2 text-right">
                          <span className={`font-medium ${f.active_loads > 0 ? 'text-blue-600' : 'text-slate-400'}`}>{f.active_loads}</span>
                        </td>
                        <td className="py-2.5 px-2 text-right font-medium text-slate-700">{f.drivers}</td>
                        <td className="py-2.5 px-2 text-right">
                          <span className="flex items-center justify-end gap-1">
                            <Users className="w-3 h-3 text-slate-400" />
                            <span className="font-medium text-slate-700">{f.agents}</span>
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right font-medium text-green-700">${f.revenue.toLocaleString()}</td>
                        <td className="py-2.5 pl-2 text-right">
                          <span className={`font-bold ${f.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            ${f.profit.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-bold text-sm">
                    <td className="pt-2 pr-3 text-slate-700">Totals</td>
                    <td colSpan="2" />
                    <td className="pt-2 px-2 text-right text-slate-700">{franchises.reduce((s, f) => s + f.carriers, 0)}</td>
                    <td className="pt-2 px-2 text-right text-slate-700">{franchises.reduce((s, f) => s + f.loads, 0)}</td>
                    <td className="pt-2 px-2 text-right text-blue-600">{franchises.reduce((s, f) => s + f.active_loads, 0)}</td>
                    <td className="pt-2 px-2 text-right text-slate-700">{franchises.reduce((s, f) => s + f.drivers, 0)}</td>
                    <td className="pt-2 px-2 text-right text-slate-700">{franchises.reduce((s, f) => s + f.agents, 0)}</td>
                    <td className="pt-2 px-2 text-right text-green-700">${franchises.reduce((s, f) => s + f.revenue, 0).toLocaleString()}</td>
                    <td className="pt-2 pl-2 text-right text-emerald-600">${franchises.reduce((s, f) => s + f.profit, 0).toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Paperwork Widget - Broker and Super Admin */}
      {(user.role === 'super_admin' || user.role === 'broker' || user.role === 'broker_manager') && paperwork && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="paperwork-widget">
          {/* Folder Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <FolderOpen className="w-5 h-5 text-yellow-600" />
                Dispatcher Folders ({new Date().getFullYear()})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{paperwork.total_folders}</p>
                  <p className="text-xs text-slate-500">Folders</p>
                </div>
                <div className="h-8 w-px bg-slate-200" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{paperwork.total_documents}</p>
                  <p className="text-xs text-slate-500">Documents</p>
                </div>
              </div>
              {paperwork.folders.length === 0 ? (
                <p className="text-sm text-slate-500">No folders yet. <a href="/folders" className="text-blue-600 hover:underline">Create one</a></p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {paperwork.folders.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium">{f.dispatcher_name}</span>
                        <span className="text-xs text-slate-400">({f.dispatcher_pin})</span>
                      </div>
                      <span className="bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">{f.document_count} docs</span>
                    </div>
                  ))}
                </div>
              )}
              <a href="/folders" className="block mt-3 text-sm text-blue-600 hover:underline text-center" data-testid="view-all-folders-link">
                View All Paperwork
              </a>
            </CardContent>
          </Card>

          {/* Recent Uploads */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Upload className="w-5 h-5 text-blue-600" />
                Recent Document Uploads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paperwork.recent_docs.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No documents uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {paperwork.recent_docs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm" data-testid={`recent-doc-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{doc.original_filename}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</span>
                            <span>PIN: {doc.dispatcher_pin}</span>
                            {doc.load_number && <span>Load: {doc.load_number}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 shrink-0 ml-2">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
